<script>
//@ts-ignore
import BaseView from "@/mixins/BaseView.vue";
import { UtilsMixin } from "@/mixins/utils.mixin";
import { MasksMixin } from "@/mixins/masks.mixin";
import ValidationMixin from "@/mixins/validation.mixin";
import FmcsaNumberInput from "@/views/companies/components/FmcsaNumberInput.vue";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import AddressField from "@/components/inputs/AddressField.vue";
import { mapActions } from "pinia";
import { useSnackbarStore } from "@/store/snackbar.store";
import debounce from "lodash.debounce";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto.ts";
import OmniSwitch from "@/components/omni/OmniSwitch.vue";

export default {
  name: "CustomerSelect",
  components: { OmniSwitch, PermissionTooltip, AddressField, OmniDialog, FmcsaNumberInput },
  mixins: [BaseView, UtilsMixin, MasksMixin, ValidationMixin],
  props: {
    name: String,
    label: String,
    modelValue: [Number, String],
    rules: Array,
    required: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: [Boolean, String],
      default: "auto",
    },
  },
  emits: ["update:modelValue", "update"],
  data() {
    return {
      model: null,
      addDialog: false,
      fmcsaTypes: ["MC", "FF", "MX"],
      sameAddress: true,
      isLoading: false,
      search: null,
      items: [],
      item: {},
      menu: false,
      isNameOverflowing: false,
      debouncedFetchCustomer: () => {},
    };
  },
  computed: {
    DispatcherPermissionValue() {
      return DispatcherPermissionValue;
    },
  },
  watch: {
    search(val) {
      this.debouncedFetchCustomer(val);
    },
    async modelValue() {
      this.model = this.modelValue;
      const item = this.items.find((u) => u.id === this.modelValue);

      if (!item) {
        this.preload(this.modelValue);
      } else {
        this.updateItem(item);
      }

      this.checkSelectedNameOverflow();
    },
    item: {
      deep: true,
      handler() {
        if (this.sameAddress) {
          this.syncAddress();
        }
      },
    },
    sameAddress() {
      if (this.sameAddress) {
        this.syncAddress();
      }
    },
  },
  async mounted() {
    this.model = this.modelValue;
    this.preload(this.modelValue);
    this.fetchData(null);

    this.debouncedFetchCustomer = debounce(this.fetchData, 300);
    this.checkSelectedNameOverflow();
  },
  methods: {
    ...mapActions(useSnackbarStore, ["showSnackbarError"]),
    countries() {
      return countries;
    },
    addCustomer() {
      this.item = {
        status: "a",
        addressCity: "",
        addressState: "",
        addressZipCode: "",
        addressCountry: null,
        billingAddressCity: "",
        billingAddressState: "",
        billingAddressZipCode: "",
        billingAddressCountry: "US",
      };
      this.addDialog = true;
    },
    onUpdateModelValue(value) {
      // console.log("on update", value);
      this.$emit("update:modelValue", value);
      const customer = this.items.find((c) => c.id === value);
      if (customer) {
        this.item = customer;
        this.$emit("update", customer);
      }
    },
    fetchData(term) {
      const params = { name: term, pageSize: 10 };

      this.isLoading = true;
      this.$api.company
        .findCustomers(params)
        .then((resp) => {
          this.items = this.mergeById(resp.data.items, this.items);

          if (this.search && this.items.length) {
            this.menu = true;
          }
        })
        .catch((err) => {
          console.error("Error loading customers", err);
        })
        .finally(() => (this.isLoading = false));
    },
    preload(id) {
      if (!id) return;

      this.isLoading = true;
      this.$api.company
        .findCustomerById(id)
        .then((resp) => {
          this.updateItem(resp.data);
        })
        .catch((err) => {
          console.error("Error loading customer", err);
        })
        .finally(() => (this.isLoading = false));
    },
    async save() {
      const result = await this.$refs.form.validate();
      if (!result.valid) return;

      if (this.sameAddress) {
        this.syncAddress();
      }

      const resp = await this.$api.company.upsertCustomer(this.item);
      if (resp.success) {
        this.addDialog = false;
        this.updateItem(resp.data);
        this.onUpdateModelValue(this.item.id);
      } else {
        this.showSnackbarError(this.$t("general.save-error"));
      }
    },
    syncAddress() {
      this.item.billingAddressLine = this.item.addressLine;
      this.item.billingAddressCity = this.item.addressCity;
      this.item.billingAddressState = this.item.addressState;
      this.item.billingAddressZipCode = this.item.addressZipCode;
      this.item.billingAddressCountry = this.item.addressCountry;
    },
    updateItem(item) {
      this.item = item;
      this.items = this.mergeById(this.items, [item]);
      this.$emit("update", item);
    },
    getBillAddress(item) {
      return `${item.billingAddressLine}, ${item.billingAddressCity}, ${item.billingAddressZipCode}, ${item.billingAddressState}`;
    },
    onFocus(focus) {
      if (!focus) {
        this.menu = false;
      }
    },
    checkSelectedNameOverflow() {
      this.isNameOverflowing = false;

      this.$nextTick(() => {
        const nameSpan = this.$refs.nameSpan;
        const selectionContainer = this.$refs.selectionContainer;

        if (nameSpan && selectionContainer) {
          this.isNameOverflowing = nameSpan.clientWidth >= selectionContainer.clientWidth;
        }
      });
    },
  },
};
</script>

<template>
  <div class="d-flex customer-select">
    <v-autocomplete
      v-model="model"
      v-model:search="search"
      :class="{ required: required }"
      :hide-details="hideDetails"
      :items="items"
      :label="label"
      :loading="isLoading"
      :menu="menu"
      :name="name"
      :rules="rules"
      autocomplete="off"
      class="mb-0 width-100 flex-shrink-1"
      clearable
      color="primary"
      density="compact"
      hide-no-data
      hide-selected
      item-title="name"
      item-value="id"
      variant="outlined"
      @update:model-value="onUpdateModelValue"
      @update:focused="onFocus"
    >
      <template #item="{ item: option, props }">
        <v-list-item :title="option.raw.name" v-bind="props">
          <span class="text-grey-darken-1">
            {{ getBillAddress(option.raw) }}
          </span>
        </v-list-item>
      </template>

      <template #selection="{ item: option }">
        <div ref="selectionContainer" class="d-flex align-center overflow-hidden">
          <span
            ref="nameSpan"
            :class="{
              'text-overflow-ellipsis overflow-hidden': isNameOverflowing,
            }"
            class="text-grey-darken-4 mr-2px white-space-nowrap"
          >
            {{ option.raw.name }},
          </span>
          <span
            v-if="!isNameOverflowing"
            class="text-grey-darken-2 overflow-hidden text-overflow-ellipsis white-space-nowrap"
          >
            {{ getBillAddress(option.raw) }}
          </span>
        </div>
      </template>
    </v-autocomplete>

    <permission-tooltip v-if="editable" :permission="DispatcherPermissionValue.CompaniesEdit">
      <template #activator="{ disabled }">
        <v-btn
          :disabled="disabled"
          class="ml-1"
          color="primary"
          icon="mdi-plus"
          size="40"
          variant="text"
          @click="addCustomer"
        ></v-btn>
      </template>

      {{ $t("permission.create-company") }}
    </permission-tooltip>

    <omni-dialog
      v-model="addDialog"
      primary-action-label="Create"
      size="large"
      title="Create company"
      @primary-action:click="save"
    >
      <div class="my-4">
        <v-form id="add-customer" ref="form" lazy-validation>
          <div class="mb-6">
            <div class="caption text-grey-darken-1 mb-4">Company information</div>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="item.name"
                  autocomplete="off"
                  class="required"
                  color="primary"
                  density="compact"
                  hide-details
                  label="Customer Name"
                  required
                  variant="outlined"
                />
              </v-col>
              <v-col>
                <v-select
                  v-model="item.fmcsaType"
                  :items="fmcsaTypes"
                  :label="$t('company.fmcsa_type')"
                  autocomplete="off"
                  color="primary"
                  density="compact"
                  hide-details="auto"
                  name="fmcsaType"
                  variant="outlined"
                />
              </v-col>
              <v-col>
                <fmcsa-number-input v-model="item.fmcsaNumber" name="fmcsaNumber" />
              </v-col>
            </v-row>
          </div>
          <div class="mb-6">
            <address-field v-model="item" hide-location label="Physical Address"></address-field>
          </div>

          <div data-qa="billing-address">
            <address-field
              v-model="item"
              :disabled="sameAddress"
              hide-location
              label="Billing Address"
              model-prefix="billing"
            >
              <template #label:suffix>
                <v-checkbox
                  v-model="sameAddress"
                  class="text-grey-darken-2 mt-0 pt-0 billing-checkbox"
                  color="primary"
                  hide-details=""
                  label="Billing address same as physical"
                />
              </template>
            </address-field>
          </div>

          <omni-switch v-model="item.sendUpdates" class="my-4">
            <template #label> <span class="text-grey-darken-2">Send order updates</span> </template>
          </omni-switch>
        </v-form>
      </div>
    </omni-dialog>
  </div>
</template>

<style lang="scss" scoped>
.dialog-btn {
  padding: 0 !important;
  margin-left: 12px;
  min-width: auto !important;
  height: auto !important;
}

.modal-close {
  position: absolute;
  top: 15px;
  right: 15px;
  cursor: pointer;
}

.customer-select {
  &:deep(.v-field__input) {
    flex-wrap: nowrap;
  }
}
</style>
